import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CheckCircle2, ArrowRight,
  Users, Database, BarChart2, Globe,
} from 'lucide-react';
import { homeCategories, tabsData, whyChooseUs } from '../data/mock';
import { useScrollReveal, useCounter } from '../hooks/useScrollAnimation';
import HeroSection from '../components/ui/hero-section-9';

/* ─────────────────────────────────────────
   HERO WRAPPER  — B2BTechLeads content
───────────────────────────────────────── */
const HomeHero = () => {
  const navigate = useNavigate();

  const heroProps = {
    title: (
      <>
        Generate Quality{' '}
        <span style={{ color: '#43a047' }}>B2B Leads</span>
        <br />& Drive Revenue
      </>
    ),
    subtitle:
      'B2BTechLeads connects you with 32M+ verified decision-makers across 40+ industry segments using data-driven, multi-channel marketing strategies.',
    actions: [
      {
        text: 'Get Started Today',
        onClick: () => navigate('/contact'),
        variant: 'default',
        className: 'text-white shadow-md',
        style: { backgroundColor: '#43a047', borderColor: '#43a047' },
      },
      {
        text: 'Learn More',
        onClick: () => navigate('/about-us'),
        variant: 'outline',
      },
    ],
    stats: [
      {
        value: '32M+',
        label: 'Global B2B Contacts',
        icon: <Globe className="h-5 w-5" style={{ color: '#43a047' }} />,
      },
      {
        value: '40+',
        label: 'Industry Segments',
        icon: <BarChart2 className="h-5 w-5" style={{ color: '#43a047' }} />,
      },
      {
        value: '30+',
        label: 'Happy Clients',
        icon: <Users className="h-5 w-5" style={{ color: '#43a047' }} />,
      },
    ],
    images: [
      'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&q=75',
      'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&q=75',
      'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=600&q=75',
    ],
  };

  return (
    <div className="border-b border-border">
      <HeroSection {...heroProps} />
    </div>
  );
};

/* ─────────────────────────────────────────
   CATEGORY CARDS
───────────────────────────────────────── */
const CategoryCards = () => {
  const [ref, visible] = useScrollReveal({ threshold: 0.1 });

  return (
    <div className="bg-background" ref={ref}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3">
          {homeCategories.map((cat, idx) => (
            <div
              key={cat.id}
              className={`group border border-border overflow-hidden ${visible ? 'anim-fade-up' : 'reveal-hidden'}`}
              style={{ animationDelay: `${idx * 0.13}s` }}
            >
              {/* Image */}
              <div className="overflow-hidden h-52" style={{ background: '#1c2a1f' }}>
                <img
                  src={cat.image}
                  alt={cat.title}
                  loading="eager"
                  className="img-zoom w-full h-full object-cover opacity-90 group-hover:opacity-100"
                />
              </div>
              {/* Content */}
              <div className="p-6 bg-card" style={{ borderTop: '3px solid #43a047' }}>
                <h3 className="text-lg font-bold text-foreground mb-4">{cat.title}</h3>
                <ul className="space-y-2">
                  {cat.links.map((link) => (
                    <li key={link.href}>
                      <Link
                        to={link.href}
                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-[#43a047] group/link"
                        style={{ transition: 'color 0.2s' }}
                      >
                        <span
                          className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: '#43a047', transition: 'transform 0.2s' }}
                        >
                          <ArrowRight size={10} className="text-white" />
                        </span>
                        <span className="group-hover/link:translate-x-0.5" style={{ display: 'inline-block', transition: 'transform 0.2s' }}>
                          {link.label}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   ANIMATED STAT ITEM
───────────────────────────────────────── */
const StatItem = ({ value, label, icon: Icon, start }) => {
  const num = parseInt(value.replace(/\D/g, ''), 10);
  const suffix = value.replace(/[0-9]/g, '');
  const count = useCounter(num, 1800, start);
  return (
    <div className={`text-center text-white ${start ? 'anim-fade-up' : 'reveal-hidden'}`}>
      <div className="flex justify-center mb-2">
        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-white/15">
          <Icon size={18} className="text-white" />
        </div>
      </div>
      <div className="text-3xl md:text-4xl font-extrabold tracking-tight">
        {start ? count : 0}{suffix}
      </div>
      <div className="text-sm font-medium text-green-100 mt-1">{label}</div>
    </div>
  );
};

/* ─────────────────────────────────────────
   STATS BANNER
───────────────────────────────────────── */
const StatsBanner = () => {
  const [ref, visible] = useScrollReveal({ threshold: 0.3 });
  const stats = [
    { value: '32M+', label: 'Global B2B Contacts', icon: Globe },
    { value: '40+',  label: 'Industry Segments',   icon: BarChart2 },
    { value: '200+', label: 'Expert Employees',     icon: Users },
    { value: '30+',  label: 'Satisfied Clients',    icon: CheckCircle2 },
  ];

  return (
    <div ref={ref} style={{ backgroundColor: '#43a047' }} className="py-12 relative overflow-hidden">
      {/* background pattern */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
        backgroundSize: '24px 24px',
      }} />
      <div className="relative max-w-6xl mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <div key={i} style={{ animationDelay: `${i * 0.12}s` }}>
              <StatItem {...s} start={visible} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   TABS + WHY CHOOSE US
───────────────────────────────────────── */
const TabsSection = () => {
  const [activeTab, setActiveTab] = useState(tabsData[0].id);
  const [ref, visible] = useScrollReveal({ threshold: 0.1 });
  const activeData = tabsData.find((t) => t.id === activeTab);

  return (
    <div className="py-14 bg-muted" ref={ref}>
      <div className="max-w-6xl mx-auto px-4">
        <div className={`text-center mb-10 ${visible ? 'anim-fade-up' : 'reveal-hidden'}`}>
          <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#43a047' }}>What We Do</p>
          <h2 className="text-3xl font-extrabold text-foreground">Our Core Services</h2>
          <div className="w-14 h-1 mx-auto mt-3 rounded-full" style={{ background: '#43a047' }} />
        </div>

        <div className={`flex flex-col lg:flex-row gap-8 ${visible ? 'anim-fade-up delay-200' : 'reveal-hidden'}`}>

          {/* Tabs */}
          <div className="flex-1">
            <div className="flex bg-card rounded-t-lg border border-border border-b-0 overflow-hidden">
              {tabsData.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 px-4 py-3 text-sm font-semibold border-b-2 -mb-px ${
                    activeTab === tab.id
                      ? 'text-[#43a047] border-[#43a047] bg-card'
                      : 'text-muted-foreground border-transparent bg-muted hover:bg-card hover:text-foreground'
                  }`}
                  style={{ transition: 'all 0.2s' }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="bg-card p-7 border border-border rounded-b-lg rounded-tr-lg shadow-sm">
              <p key={activeTab} className="anim-fade-in text-muted-foreground leading-relaxed text-sm mb-5">
                {activeData.content}
              </p>
              <Link
                to={activeData.link}
                className="inline-flex items-center gap-1.5 text-sm font-semibold group/link"
                style={{ color: '#43a047' }}
              >
                Learn More
                <ArrowRight size={14} className="group-hover/link:translate-x-1" style={{ transition: 'transform 0.2s' }} />
              </Link>
            </div>
          </div>

          {/* Why Choose Us */}
          <div className="lg:w-80">
            <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden h-full">
              <div className="px-6 py-4 flex items-center gap-2" style={{ borderBottom: '3px solid #43a047' }}>
                <CheckCircle2 size={18} style={{ color: '#43a047' }} />
                <h3 className="text-base font-bold text-foreground">Why Choose Us</h3>
              </div>
              <div className="p-6">
                <ul className="space-y-3">
                  {whyChooseUs.map((item, i) => (
                    <li
                      key={i}
                      className={`flex items-start gap-2.5 ${visible ? 'anim-fade-up' : 'reveal-hidden'}`}
                      style={{ animationDelay: `${0.3 + i * 0.07}s` }}
                    >
                      <span
                        className="mt-1 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: '#43a047' }}
                      >
                        <ArrowRight size={8} className="text-white" />
                      </span>
                      <span className="text-sm text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  <Link
                    to="/contact"
                    className="block text-center text-white text-sm font-semibold py-3 rounded-full shadow-sm"
                    style={{ backgroundColor: '#43a047', transition: 'opacity 0.2s, transform 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                  >
                    Get In Touch
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   SERVICES SHOWCASE
───────────────────────────────────────── */
const servicesShowcase = [
  { icon: Globe,     title: 'Marketing Leads',    href: '/marketing-leads',    desc: 'Multi-channel leads — telemarketing, email, postal, and all-in-one packages.' },
  { icon: Database,  title: 'Database Assessment', href: '/database-assessment', desc: 'Cleanse, enrich and verify your B2B database for maximum campaign impact.' },
  { icon: BarChart2, title: 'Market Survey',       href: '/market-survey',       desc: 'Deep market research and competitive insights tailored to your industry.' },
  { icon: Users,     title: 'Technology Leads',    href: '/technology-leads',    desc: 'Reach CIOs, CTOs, and IT decision-makers actively evaluating solutions.' },
];

const ServicesShowcase = () => {
  const [ref, visible] = useScrollReveal({ threshold: 0.1 });
  return (
    <div className="py-16 bg-background" ref={ref}>
      <div className="max-w-6xl mx-auto px-4">
        <div className={`text-center mb-12 ${visible ? 'anim-fade-up' : 'reveal-hidden'}`}>
          <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#43a047' }}>Services</p>
          <h2 className="text-3xl font-extrabold text-foreground">How We Help You Grow</h2>
          <div className="w-14 h-1 mx-auto mt-3 rounded-full" style={{ background: '#43a047' }} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {servicesShowcase.map((svc, i) => (
            <Link
              key={i}
              to={svc.href}
              className={`card-hover group p-6 border border-border rounded-xl bg-card text-left ${visible ? 'anim-fade-up' : 'reveal-hidden'}`}
              style={{ animationDelay: `${i * 0.1}s`, textDecoration: 'none' }}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: '#e8f5e9', transition: 'background 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#43a047'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#e8f5e9'}
              >
                <svc.icon size={22} style={{ color: '#43a047', transition: 'color 0.2s' }} />
              </div>
              <h4 className="font-bold text-foreground mb-2 text-sm">{svc.title}</h4>
              <p className="text-xs text-muted-foreground leading-relaxed mb-3">{svc.desc}</p>
              <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: '#43a047' }}>
                Learn More <ArrowRight size={12} style={{ transition: 'transform 0.2s' }} className="group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   CTA SECTION
───────────────────────────────────────── */
const CTASection = () => {
  const [ref, visible] = useScrollReveal({ threshold: 0.2 });
  return (
    <div
      ref={ref}
      className="py-20 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #1c2a1f 0%, #2d3a2e 100%)' }}
    >
      {/* pattern */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'radial-gradient(circle, #43a047 1px, transparent 1px)',
        backgroundSize: '30px 30px',
      }} />
      <div
        className="absolute -top-16 -right-16 w-64 h-64 rounded-full opacity-10"
        style={{ background: '#43a047' }}
      />

      <div className="relative max-w-6xl mx-auto px-4 text-center">
        <p className={`text-xs font-bold uppercase tracking-widest mb-3 ${visible ? 'anim-fade-up' : 'reveal-hidden'}`} style={{ color: '#43a047' }}>
          Get Started Today
        </p>
        <h2 className={`text-3xl md:text-4xl font-extrabold text-white mb-4 ${visible ? 'anim-fade-up delay-100' : 'reveal-hidden'}`}>
          Ready to Grow Your Business?
        </h2>
        <p className={`text-green-100 mb-10 max-w-xl mx-auto ${visible ? 'anim-fade-up delay-200' : 'reveal-hidden'}`}>
          Partner with B2BTechLeads and access our database of 32 Million+ verified B2B contacts across 40+ industry segments.
        </p>
        <div className={`flex flex-col sm:flex-row gap-4 justify-center ${visible ? 'anim-fade-up delay-300' : 'reveal-hidden'}`}>
          <Link
            to="/contact"
            className="btn-pulse inline-flex items-center justify-center gap-2 text-white font-bold px-9 py-3.5 rounded-full shadow-lg text-sm"
            style={{ backgroundColor: '#43a047', transition: 'opacity 0.2s, transform 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            Contact Us Today <ArrowRight size={16} />
          </Link>
          <Link
            to="/about-us"
            className="inline-flex items-center justify-center gap-2 font-bold px-9 py-3.5 rounded-full text-sm border-2 border-white/30 text-white"
            style={{ transition: 'background 0.2s, border-color 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#43a047'; e.currentTarget.style.color = '#43a047'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; e.currentTarget.style.color = '#fff'; }}
          >
            Learn About Us
          </Link>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   HOME PAGE
───────────────────────────────────────── */
const Home = () => (
  <div className="page-enter">
    <HomeHero />
    <CategoryCards />
    <TabsSection />
    <StatsBanner />
    <ServicesShowcase />
    <CTASection />
  </div>
);

export default Home;
